import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnChanges, SimpleChanges, Injectable } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Observable, of, forkJoin } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { DateAdapter, MAT_DATE_FORMATS, NativeDateAdapter } from '@angular/material/core';
import { MaterialModule } from '../../../shared/material.module';
import { UserService } from '../../../services/user.service';
import { AppUserBase } from '../../../models/appUserBase';
import { VoteResultsCategory } from '../../../models/voteResultsCategory';
import { LutService } from '../../../services/lut.service';
import { CompanyVoteResultSearchFilter } from '../../../models/companyVoteResultSearchFilter';
import { CountryFlat } from '../../../models/country';
import { Helpers } from '../../../utilities/helpers';
import { REGIONS, RegionOption } from '../../../utilities/regions';

const DD_MM_YYYY_FORMAT = {
  parse: { dateInput: 'DD/MM/YYYY' },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  override parse(value: any): Date | null {
    if (!value) return null;

    if (typeof value === 'string') {
      const parts = value.split('/');
      if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);

        if (!isNaN(day) && !isNaN(month) && !isNaN(year)) {
          return new Date(year, month, day);
        }
      }
    }

    return super.parse(value);
  }

  override format(date: Date, displayFormat: Object): string {
    if (!date) return '';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }
}

@Component({
  selector: 'app-company-vote-result-search-box',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,
    MatIconModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    FormsModule
  ],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: DD_MM_YYYY_FORMAT }
  ],
  templateUrl: './company-vote-result-search-box.component.html',
  styleUrl: './company-vote-result-search-box.component.scss'
})
export class CompanyVoteResultSearchBoxComponent implements OnInit, OnChanges {
  @Input() isSearchVisible: boolean = false;
  @Input() initialFilter: CompanyVoteResultSearchFilter = {};

  regions: RegionOption[] = REGIONS;
  years: number[] = [];

  @Output() searchFilter = new EventEmitter<CompanyVoteResultSearchFilter>();

  categories$!: Observable<VoteResultsCategory[] | null>;
  subCategories$: Observable<VoteResultsCategory[]> = of([]);
  allCategories: VoteResultsCategory[] = [];
  countries$!: Observable<CountryFlat[]>;
  allCountries: CountryFlat[] = [];
  users$!: Observable<AppUserBase[]>;

  searchForm: {
    region?: number;
    countryId?: string;
    companyName?: string;
    year?: number;
    categoryId?: string;
    subcategoryId?: string;
    dateFrom?: string;
    dateTo?: string;
    createdBy?: string;
    isShareholderProposal?: boolean;
  } = {
    region: undefined,
    countryId: undefined,
    companyName: undefined,
    year: undefined,
    categoryId: undefined,
    subcategoryId: undefined,
    dateFrom: undefined,
    dateTo: undefined,
    createdBy: undefined,
    isShareholderProposal: undefined
  };

  constructor(private lutService: LutService, private userService: UserService) {
    const startYear = Number(Helpers.defaultMeasurementYear());
    for (let year = startYear; year >= 2007; year--) {
      this.years.push(year);
    }
  }

  ngOnInit(): void {
    const categoriesData$ = this.lutService.getVoteResultsCategories().pipe(
      tap(categories => {
        this.allCategories = categories;
      }),
      shareReplay(1)
    );

    const countriesData$ = this.lutService.getVisibleCountries().pipe(
      tap(countries => {
        this.allCountries = countries;
      }),
      shareReplay(1)
    );

    forkJoin({
      categories: categoriesData$,
      countries: countriesData$
    }).subscribe(() => {
      this.populateSearchForm();
    });

    this.categories$ = categoriesData$.pipe(
      map(categories => categories
        .filter(category => category.parentId === null)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      )
    );

    this.countries$ = countriesData$.pipe(
      map(countries => countries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })))
    );

    this.users$ = this.userService.getUsers(1, 100, 'FullName', true).pipe(
      map(response => response.data)
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialFilter'] && !changes['initialFilter'].firstChange) {
      this.populateSearchForm();
    }
  }

  private populateSearchForm(): void {
    if (this.initialFilter && Object.keys(this.initialFilter).length > 0) {
      this.searchForm = {
        region: this.initialFilter.region,
        countryId: this.initialFilter.countryId,
        companyName: this.initialFilter.companyName,
        year: this.initialFilter.year,
        categoryId: this.initialFilter.categoryId,
        subcategoryId: this.initialFilter.subcategoryId,
        dateFrom: this.initialFilter.dateFrom,
        dateTo: this.initialFilter.dateTo,
        createdBy: this.initialFilter.createdBy,
        isShareholderProposal: this.initialFilter.isShareholderProposal
      };

      if (this.initialFilter.categoryId) {
        this.subCategories$ = of(
          this.allCategories
            .filter(category => category.parentId === this.initialFilter.categoryId)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }

      if (this.initialFilter.region) {
        this.countries$ = of(
          this.allCountries
            .filter(country => country.region === this.initialFilter.region)
            .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
        );
      }
    }
  }

  onRegionChange(): void {
    if (this.searchForm.region) {
      this.countries$ = of(
        this.allCountries
          .filter(country => country.region === this.searchForm.region)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.countries$ = of(
        this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
      this.searchForm.countryId = undefined;
    }
  }

  onCategoryChange(): void {
    if (this.searchForm.categoryId) {
      this.subCategories$ = of(
        this.allCategories
          .filter(category => category.parentId === this.searchForm.categoryId)
          .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      );
    } else {
      this.subCategories$ = of([]);
      this.searchForm.subcategoryId = undefined;
    }
  }

  onSearch(): void {
    this.searchFilter.emit({ ...this.searchForm });
  }

  clearSearch(): void {
    this.searchForm = {
      region: undefined,
      countryId: undefined,
      companyName: undefined,
      year: undefined,
      categoryId: undefined,
      subcategoryId: undefined,
      dateFrom: undefined,
      dateTo: undefined,
      createdBy: undefined,
      isShareholderProposal: undefined
    };
    this.subCategories$ = of([]);
    this.countries$ = of(
      this.allCountries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
    );
    this.searchFilter.emit({ ...this.searchForm });
  }
}
